import { Component, HostBinding, HostListener, Inject, ViewChild } from '@angular/core';
import { UserResponse } from '../../services/auth/responses/user.response';
import { IConfigurationService } from '../../../core/services/configuration/configuration-service.interface';
import { EventsServiceInterface } from '../../../core/services/events/events.interface';
import { ISidebarService } from '../../../core/services/sidebar/sidebar.interface';
import { IMenuItem } from '@platform500services/p500-ui-kit';
import { Router } from '@angular/router';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { MENU_CONFIG } from './sidebar.config-menu';
import { RbacServiceInterface } from '../../services/rbac/rbac-service.interface';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { UntypedFormControl } from '@angular/forms';
import { IOnboardingTipsService } from '../../services/onboarding-tips/onboarding-tips.interface';
import { debounceTime } from 'rxjs/operators';
import { changelogData } from '../../../../changelog';
import { RbacPermissionsConstant } from '../../services/rbac/rbac-permissions.constant';
import { Row } from '../table-facade/data.response';
import { IEnvironment } from '../../services/environment/environment.interface';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { BrowserWindowRef } from '../../services/window/window.service';

@Component({
  selector: 'gtd-sidebar',
  templateUrl: './sidebar.component.html',
})
@DestroySubscribers()
export class SidebarComponent {
  public sidebarOverlay = false;
  private subscribers: any = {};
  public user: UserResponse;
  @HostBinding('class') className = 'c-sidebar';
  @HostBinding('class.-open-selector') public isOpenSelector = false;
  @ViewChild('searchInner') insideElement;
  public menu: IMenuItem[];
  public isCollapsedNavigation = false;
  public currentSection?: IMenuItem;
  public sidebarMenu: IMenuItem[];
  public changelogVersion: string;
  public searchData: IMenuItem[];
  public onboardingStatus = new UntypedFormControl();
  public showOnboardingButton: boolean;
  public subAffiliates: Row[] = [];
  public year: string;
  public hideCopyright = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject('IConfigurationService') public configurationService: IConfigurationService,
    @Inject('ISidebarService') public sidebarService: ISidebarService,
    @Inject('RbacServiceInterface') private rbacService: RbacServiceInterface,
    @Inject('AuthServiceInterface') public authService: AuthServiceInterface,
    @Inject('IOnboardingTipsService') private onboardingTipsService: IOnboardingTipsService,
    @Inject('IEnvironmentService') private envService: IEnvironment,
    private window: BrowserWindowRef
  ) {
    this.user = this.authService.userData();
    if (this.user) {
      this.menu = this.filterMenu(MENU_CONFIG).filter((item) => item.link || (item.children && item.children.length));
      this.sidebarMenu = this.menu;
    }

    this.subscribers.isShownSidebar = this.eventsService.on('sidebarComponent:toggle').subscribe(() => {
      this.sidebarOverlay = !this.sidebarOverlay;
    });

    this.onboardingStatus.patchValue(JSON.parse(localStorage.getItem('isOnboardingWorking')));
    this.showOnboardingButton = this.onboardingTipsService.getWorkingStatus();
    this.onboardingStatus.valueChanges.subscribe((status: boolean) => {
      this.onboardingTipsService.setWorkingStatus(status);
    });
    this.onboardingTipsService
      .stateChanges()
      .pipe(debounceTime(2000))
      .subscribe(() => {
        if (this.onboardingTipsService.getWorkingStatus() !== this.onboardingStatus.value) {
          this.onboardingStatus.patchValue(this.onboardingTipsService.getWorkingStatus());
        }
        this.showOnboardingButton = this.onboardingTipsService.getWorkingStatus();
      });
    this.changelogVersion = changelogData[0]?.version;

    const masterToken = localStorage.getItem('master-affiliate-token');
    const affiliateToken = localStorage.getItem('affiliate-token');
    if (this.rbacService.can(RbacPermissionsConstant.PAGES_TABLE_AFFILIATES_AREA_TABLE)) {
      const apiURL = (this.envService.getVar('services.api') as string) + '/table/data';
      if (masterToken) {
        localStorage.setItem('affiliate-token', masterToken);
      }
      try {
        this.http
          .post<{ data: { rows: Row[] } }>(apiURL, {
            table: 'AffiliateCrm\\AffiliatesAffAreaTable',
            page: 1,
            limit: 200,
            filters: {},
            order_values: {},
          })
          .subscribe((response) => {
            this.subAffiliates = response.data.rows;
          });
        if (masterToken) {
          localStorage.setItem('affiliate-token', affiliateToken);
        }
      } catch {
        localStorage.removeItem('master-affiliate-token');
      }
    }

    this.year = moment().format('Y');
    this.initWhiteLabelOptions();
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:touch', ['$event'])
  handleOutsideClick() {
    this.closeSection();
  }

  filterMenu(menuItems: IMenuItem[]): IMenuItem[] {
    return menuItems.reduce((list, item: IMenuItem) => {
      if (this.canMenuItem(item)) {
        return item.children ? [...list, { ...item, children: [this.filterMenu(item.children)] }] : [...list, item];
      } else {
        return list;
      }
    }, []);
  }

  canMenuItem(menuItem: IMenuItem): boolean {
    if (menuItem.showOnlyDemo && !this.configurationService.data().isDemo) {
      return false;
    }
    if (!menuItem.permission) {
      return true;
    }

    return this.rbacService.can(menuItem.permission);
  }

  toggleSidebar(): void {
    if (this.sidebarOverlay) {
      this.eventsService.broadcast('sidebarComponent:toggle');
    }
  }

  toggleNavigation() {
    this.isCollapsedNavigation = !this.isCollapsedNavigation;
    this.eventsService.broadcast('sidebarComponent:collapsed-toggle', this.isCollapsedNavigation);
    this.closeSection();
  }

  openSection(section: IMenuItem, $event: MouseEvent) {
    $event.stopPropagation();
    this.currentSection = section;

    if (section.link) {
      this.router.navigateByUrl(section.link);
    }
  }

  closeSection() {
    this.currentSection = null;
  }

  isActiveSection(section: IMenuItem) {
    return (
      (section.link && this.router.isActive(section.link, false)) ||
      section.children?.some((item) => item.some((deepItem) => this.router.isActive(deepItem.link, false)))
    );
  }

  selectSearchItem(item) {
    this.router.navigate([item.link]);
  }

  public installPwa() {
    (this.window.nativeWindow.installPwaEvent as any).prompt();
  }

  private initWhiteLabelOptions(): void {
    try {
      const settings = JSON.parse(
        this.configurationService.data().brand.affiliate_crm_signin_signup_pages_settings
      ).affiliate_crm_login_settings;
      this.hideCopyright = settings.affiliate_crm_white_label;
    } catch {}
  }
}
